import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import type { TimeEntryDocType } from '@ast-data/shared';
import {
    GridFooterContainer,
    useGridApiContext,
    useGridSelector,
    gridFilteredSortedRowEntriesSelector
} from '../../lib/dataGrid';
import { entryNetMinutes, formatClock } from './timeMath';

/**
 * One total: an icon and its value. The icon carries the meaning, so the label it
 * replaces becomes the tooltip AND the group's accessible name — an icon alone
 * reads as nothing to a screen reader, and there is no hover on a phone.
 */
function Total({
    icon,
    label,
    value
}: {
    icon: ReactNode;
    label: string;
    value: ReactNode;
}) {
    return (
        <Tooltip title={label}>
            <Box
                aria-label={label}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary'
                }}
            >
                {icon}
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.primary'
                    }}
                >
                    {value}
                </Typography>
            </Box>
        </Tooltip>
    );
}

/**
 * Footer for both Zeiterfassung grids: how many entries are in view and their
 * net total. Replaces the grid's default row-count footer.
 *
 * It reads the rows from the grid rather than taking them as props, so the
 * office grid's totals follow its filters — only what the user can see is
 * counted. The field grid has no filters, so there "in view" is the day's rows.
 */
export default function TimeEntryTotalsFooter() {
    const intl = useIntl();
    const apiRef = useGridApiContext();
    const rows = useGridSelector(apiRef, gridFilteredSortedRowEntriesSelector);

    // A row without times adds nothing — entryNetMinutes returns null, which is
    // also the whole story for a Gerät booked for a day without hours. What a
    // row DOES contribute counts once per piece (Anzahl × Spanne).
    const totalMinutes = rows.reduce((sum, entry) => {
        const row = entry.model as TimeEntryDocType;
        return sum + (entryNetMinutes(row) ?? 0);
    }, 0);

    return (
        <GridFooterContainer sx={{ px: 1.5, gap: 2, justifyContent: 'center' }}>
            <Total
                icon={<PeopleOutlineIcon fontSize="small" />}
                label={intl.formatMessage({ id: 'timeEntry.totals.entries' })}
                value={rows.length}
            />
            <Total
                icon={<AccessTimeIcon fontSize="small" />}
                label={intl.formatMessage({ id: 'timeEntry.totals.hours' })}
                value={formatClock(totalMinutes)}
            />
        </GridFooterContainer>
    );
}
